import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, QueryList, ViewChildren, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { PhxCheckboxComponent } from '../../../../shared/components/phx-input-checkbox/phx-input-checkbox.component';
import { PhxSelect } from '../../../../shared/components/phx-select/phx-select.component';
import { EditLinks } from '../../../../@core/constants/edit-links';
import { BaseListCombinedService } from '../../../../@core/services/base-list-combined.service';
import { Condition, Facet, FacetValue, Operator, Product, ProductInput, ProductType, ProductVariantToFacetValueLink, SearchRule } from '../../../../generated/generated-types';
import { GenericEnumDropDownComponent } from '../../../../shared/controls/generic-enum-drop-down/generic-enum-drop-down.component';
import { FacetValueService } from '../../../../shared/services/facet-value.service';
import { FacetDropDownComponent } from '../../controls/facet-drop-down/facet-drop-down.component';
import { ProductDropDownComponent } from '../../modules/product-chooser/product-dropdown.component';
import { ProductVariantToFacetValueLinkService } from '../../services/product-variant-to-facet-value-link.service';
import { ProductService } from '../../services/product.service';
import { WidgetFacetValueListComponent } from '../widget-facet-value-list/widget-facet-value-list.component';

/**
 * vX property name counts up like v1, v2, v3, ...
 */
interface ProductVariantFacets {
  [vX: string]: FacetValue;
}

interface FacetVariantWithProduct {
  variant: ProductVariantFacets;
  existingProductVariant?: Product;
  originalProductVariant?: Product;
}

interface FacetValueLinks {
  variantProductId: string;
  facetValueLinks: ProductVariantToFacetValueLink[];
}

interface FacetHolder {
  facet: Facet | null;
  selectedValues: FacetValue[];
}

interface DelimiterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-product-variant-widget',
  templateUrl: './product-variant-widget.component.html',
  styleUrl: './product-variant-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductService],
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    TooltipModule,
    TranslatePipe,
    ButtonModule,
    FacetDropDownComponent,
    WidgetFacetValueListComponent,
    ProductDropDownComponent,
    PhxCheckboxComponent,
    PhxSelect,
    FormsModule,
    ProgressSpinnerModule,
    GenericEnumDropDownComponent
  ]
})
export class ProductVariantWidgetComponent {

  selectedFacets: FacetHolder[] = [];
  allVariants = signal<FacetVariantWithProduct[]>([]);
  productVariants: Product[] = [];
  productIdentifierArray = [];
  activeFacetHoldersSet = false;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // Delimiter functionality
  delimiterOptions = signal<DelimiterOption[]>([
    { label: this.baseServices.i18nService.translate(_("common.delimiter-none")), value: '' },
    { label: this.baseServices.i18nService.translate(_("common.delimiter-dash")), value: '-' },
    { label: this.baseServices.i18nService.translate(_("common.delimiter-dot")), value: '.' }
  ]);

  // Store delimiter selection for each facet position
  facetDelimiters = signal<Record<number, string>>({});

  generateProductType = signal<ProductType>(ProductType.BASIC);

  // Enable/disable generate products mode
  generateProductsEnabled = signal<boolean>(false);

  // Generation state tracking
  isGenerating = signal<boolean>(false);
  generationResults = signal<Record<string, { status: 'generating' | 'success' | 'error', message?: string, productId?: string }>>({});

  // Computed signal for variant previews
  variantPreviews = computed(() => {
    const variants = this.allVariants();
    const delimiters = this.facetDelimiters();
    const baseIdentifier = this.baseProduct?.identifier || 'P01';
    const enabled = this.generateProductsEnabled();
    const results = this.generationResults();

    return variants.map(variant => {
      const variantKey = this.getVariantKey(variant);
      const result = results[variantKey];

      if (result) {
        return { variant, preview: '', result };
      }

      return {
        variant,
        preview: enabled && !variant.existingProductVariant?.id ?
          this.generateVariantPreview(variant, baseIdentifier, delimiters) :
          '',
        result: null
      };
    });
  });

  @Input()
  baseProduct: Product;

  @ViewChildren('productDropdown') productDropdowns: QueryList<ProductDropDownComponent>;

  constructor(
    public cdRef: ChangeDetectorRef,
    public baseServices: BaseListCombinedService,
    private productService: ProductService,
    private productVariantToFacetValueService: ProductVariantToFacetValueLinkService,
    private facetValueService: FacetValueService
    //private facetService: FacetService

  ) {
    this.reset();
  }

  async ngOnChanges() {
    if (this.baseProduct?.id && !this.activeFacetHoldersSet) {
      this.loading$.next(true);
      await this.loadActiveFacetHolders();
      this.loading$.next(false);
      this.activeFacetHoldersSet = true;
    }
  }

  addFacetButtonClick() {
    this.selectedFacets.push({} as any);
  }

  canAddFacet(): boolean {
    if (this.selectedFacets.length === 0) {
      return false;
    }
    const lastFacet = this.selectedFacets[this.selectedFacets.length - 1];
    // Can add a new facet only if the last one has both a facet selected and at least one value selected
    return !!(lastFacet?.facet?.id && lastFacet?.selectedValues?.length > 0);
  }

  reset() {
    this.selectedFacets = [];
    if (this.selectedFacets.length === 0) {
      this.selectedFacets.push({} as any); //first empty one to have dynamic number of facets
    }
  }

  changeFacet(holder: FacetHolder, facet: Facet | null | undefined, facetDropDown: FacetDropDownComponent) {

    // If facet is cleared (null/undefined), clear the holder
    if (!facet) {
      holder.facet = null;
      holder.selectedValues = [];
      this.updateAllVariants();
      this.cdRef.markForCheck();
      return;
    }

    //check if we have this facet in the selectedFacets array
    //x can be empty if not set yet
    if (this.selectedFacets.find(x => x?.facet?.id === facet?.id && x !== holder)) {
      facetDropDown.clear();
      return;
    }

    holder.facet = facet;
    holder.selectedValues = [];

    this.updateAllVariants();
    this.cdRef.markForCheck();

  }

  async onSelectedFacetValuesChange(facetValues: FacetValue[], holder: FacetHolder) {
    holder.selectedValues = facetValues;
    this.updateAllVariants();
  }

  async onDeleteFacet(index: number, facetDropdown: any) {
    facetDropdown.clear();
    this.selectedFacets.splice(index, 1);
    this.selectedFacets = [...this.selectedFacets];

    if (this.selectedFacets.length === 0) {
      this.reset();
    }

    this.updateAllVariants();
  }

  async updateAllVariants() {
    const variants = await this.generateAllPossibleVariants();
    this.allVariants.set(variants);
  }

  getValuesOfFacet(item: Facet | Facet[] | string | string[]) {

    if (typeof item === 'string') return null;
    if (item instanceof Array) {
      if (typeof item[0] === 'string') return null;
    }

    if (item instanceof Array) {
      if (typeof item[0] === 'object')
        return item[0].values;
    }
    else
      return item.values;
  }

  onRowToggle(event: any, item: FacetValue, table: Table, expanded: boolean) {
    if (table.expandedRowKeys[item.id] && table.expandedRowKeys[item.id] === true)
      delete table.expandedRowKeys[item.id]
    else table.expandedRowKeys[item.id] = true;
    expanded = !expanded;
  }

  async generateAllPossibleVariants(): Promise<FacetVariantWithProduct[]> {
    let selectedFacetsWithValues = this.selectedFacets.filter(x => x.facet);

    let variants: ProductVariantFacets[] = [];
    let facetVariantsWithProduct: FacetVariantWithProduct[] = [];

    // if (selectedFacetsWithValues.length > 2) {
    const facetValues = selectedFacetsWithValues.map(fh => fh.selectedValues);
    const combinations = this.getCombinations(facetValues);

    combinations.forEach(combination => {

      const variantX: any = {

      };
      selectedFacetsWithValues.forEach((facet, index) => {
        variantX[`v${index + 1}`] = combination[index];
      });

      variants.push(variantX);
    });

    facetVariantsWithProduct = await Promise.all(variants.map(async (variant) => {

      let existingProductForVariant: Product = {} as Product;
      try {
        existingProductForVariant = await this.getProductVariant(variant);

      } catch (error) {
        console.log(error);

      }

      //try catch (error) {
      //   existingProductForVariant = {} as Product;
      // }

      return { variant, existingProductVariant: existingProductForVariant, originalProductVariant: existingProductForVariant } as FacetVariantWithProduct;

    }));

    return facetVariantsWithProduct;
  }

  getCombinations(arrays: any[][]): any[][] {
    const result: any[][] = [];

    const helper = (current: any[], index: number) => {
      if (index === arrays.length) {
        result.push(current);
        return;
      }

      const array = arrays[index];
      for (let i = 0; i < array.length; i++) {
        helper([...current, array[i]], index + 1);
      }
    };

    helper([], 0);

    return result;
  }

  async saveVariant(variant: FacetVariantWithProduct, multiple?: boolean) {

    if (!this.baseProduct) {
      this.baseServices.notificationService.error(this.baseServices.i18nService.translate(_("product-variant.no-base-product-selected")));
      return;
    }

    let changedProduct: Product = null;

    let isChanged = variant.existingProductVariant?.id != variant.originalProductVariant?.id;

    if (!isChanged)
      return;


    let facetValuesToSave: ProductVariantToFacetValueLink[] = [];
    if (variant.variant?.v1?.id) {
      facetValuesToSave.push({
        variantProductId: variant.existingProductVariant.id,
        facetValueId: variant.variant.v1.id
      });
    }
    if (variant.variant?.v2?.id) {
      facetValuesToSave.push({
        variantProductId: variant.existingProductVariant.id,
        facetValueId: variant.variant.v2.id
      });
    }
    if (variant.variant?.v3?.id) {
      facetValuesToSave.push({
        variantProductId: variant.existingProductVariant.id,
        facetValueId: variant.variant.v3.id
      });
    }

    if (facetValuesToSave.length > 0) {
      await this.productVariantToFacetValueService.saveItems(facetValuesToSave);
    } else {
      console.warn('No valid facet values found for variant:', variant);
    }

    let productInput = {
      id: variant.existingProductVariant.id,
      variantParentId: this.baseProduct.id
    } as ProductInput;
    changedProduct = simpleDeepClone(await this.productService.saveItem(productInput));
    variant.existingProductVariant = changedProduct;
    variant.originalProductVariant = changedProduct;

    if (!multiple)
      this.baseServices.notificationService.success(this.baseServices.i18nService.translate(_("common.save-successful")));
    this.cdRef.detectChanges();

  }

  async saveAllVariants() {
    if (!this.baseProduct?.id) {
      this.baseServices.notificationService.error(this.baseServices.i18nService.translate(_("product-variant.no-base-product-selected")));
      return;
    }

    // Get all existing product variants for this base product
    const allExistingVariants = await this.getAllProductVariantsOfProduct(this.baseProduct.id);

    // Get all current variant product IDs that should be kept
    const currentVariantIds = new Set(
      this.allVariants()
        .filter(v => v.existingProductVariant?.id)
        .map(v => v.existingProductVariant.id)
    );

    // Delete links for variants that are no longer in the current selection
    for (const existingVariant of allExistingVariants) {
      if (!currentVariantIds.has(existingVariant.id)) {
        // This variant is no longer represented in the current facet combinations
        const orphanedVariant: FacetVariantWithProduct = {
          variant: {} as any,
          existingProductVariant: existingVariant,
          originalProductVariant: existingVariant
        };
        await this.deleteLinks(orphanedVariant, null, true, false);
      }
    }

    // Save all current variants
    for (let variant of this.allVariants()) {
      if (variant.existingProductVariant)
        await this.saveVariant(variant, true);
    }

    this.baseServices.notificationService.success(this.baseServices.i18nService.translate(_("common.save-successful")));
  }

  async deleteLinks(variant: FacetVariantWithProduct, productDropdown?: ProductDropDownComponent, multiple?: boolean, fullDeletion?: boolean) {
    if (variant.existingProductVariant?.id) {

      //remove variantParentId
      let productInput = {
        id: variant.existingProductVariant.id,
        variantParentId: null
      } as ProductInput;

      await this.productService.saveItem(productInput);

      let productVariantToFacetValueLinks: ProductVariantToFacetValueLink[] = (await lastValueFrom(this.productVariantToFacetValueService.getItems({
        searchFilter: {
          rules: [
            {
              field: 'variantProductId',
              operator: Operator.equal,
              value: variant.existingProductVariant.id,
              condition: Condition.AND
            }
          ]
        }
      }).single$)).getProductVariantToFacetValueLinkList.items as ProductVariantToFacetValueLink[];

      if (productVariantToFacetValueLinks.length > 0) {
        for (let link of productVariantToFacetValueLinks) {
          await this.productVariantToFacetValueService.deleteItem(link.id);
        }
      }
    }

    if (!fullDeletion) {
      variant.existingProductVariant = null;
      variant.originalProductVariant = null;
    }


    if (productDropdown)
      productDropdown.clear();

    if (!multiple && !fullDeletion) {
      this.baseServices.notificationService.success(this.baseServices.i18nService.translate(_("common.deleted")));

    }
    this.cdRef.detectChanges();
  }

  async deleteAllLinks() {
    for (let variant of this.allVariants()) {
      if (variant.existingProductVariant)
        await this.deleteLinks(variant, null, true);
      this.productDropdowns.forEach(productDropdown => {
        productDropdown.clear();
      })
    }
    this.baseServices.notificationService.success(this.baseServices.i18nService.translate(_("common.deleted")));
  }

  openInNewTab(variant: FacetVariantWithProduct) {
    window.open(EditLinks.PRODUCT + variant.existingProductVariant.id, '_blank');
  }

  //for now we dont delete variants, we only remove the link

  // async deleteVariant(variant: FacetVariantWithProduct, productDropdown?: ProductDropDownComponent, multiple?: boolean) {

  //   await this.deleteLinks(variant, productDropdown, multiple, true);

  //   await this.productService.deleteItem(variant.existingProductVariant.id);

  //   this.productDropdowns.forEach(productDropdown => {
  //     productDropdown.refresh();
  //   })

  //   if (!multiple)
  //     this.baseServices.notificationService.success("Verbindungen & Variante gelöscht");

  //   variant.existingProductVariant = null;
  //   variant.originalProductVariant = null;

  //   this.cdRef.detectChanges();
  // }

  // async deleteAllVariants() {
  //   this.allVariants().forEach(async variant => {
  //     if (variant.existingProductVariant)
  //       await this.deleteVariant(variant, null, true);
  //   });
  //   this.baseServices.notificationService.success("Verbindungen & Varianten gelöscht");
  // }

  async getAllProductVariantsOfProduct(productId: string) {
    return simpleDeepClone(await lastValueFrom(await this.productService.getItems({
      searchFilter: {
        rules: [{ field: 'variantParentId', operator: Operator.equal, value: productId }]
      }
    }).mapSingle(x => x.getProducts.items as Product[])));
  }

  async getProductVariant(variant: ProductVariantFacets) {

    let productVariants: Product[] = [];

    if (this.baseProduct) {
      productVariants = simpleDeepClone(await this.getAllProductVariantsOfProduct(this.baseProduct.id));
    }

    let productIds = []
    if (productVariants?.length > 0) {
      productIds = productVariants.map(x => x.id);
    }

    let rules: SearchRule[] = [];

    if (productIds.length > 0) {

      rules.push({
        field: 'variantProductId',
        condition: Condition.AND,
        operator: Operator.in,
        value: JSON.stringify(productIds)
      })

      if (variant?.v1?.id || variant?.v2?.id || variant?.v3?.id) {

        let variantIds = [variant?.v1?.id, variant?.v2?.id, variant?.v3?.id].filter(x => x);

        rules.push({
          field: 'facetValueId',
          condition: Condition.AND,
          operator: Operator.in,
          value: JSON.stringify(variantIds),
        });

      }
      else {
        //this.baseServices.notificationService.error(this.baseServices.i18nService.translate(_("product-variant.no-facet-value-existing")));
        console.warn('No facet value existing for variant:', variant);
        return null;
      }
    }

    if (variant && productIds.length > 0) {
      let facetValueLinks: ProductVariantToFacetValueLink[] = (await lastValueFrom(this.productVariantToFacetValueService.getItems({
        searchFilter: {
          rules: rules
        }
      }).single$)).getProductVariantToFacetValueLinkList.items as ProductVariantToFacetValueLink[];

      facetValueLinks = simpleDeepClone(facetValueLinks);

      let variantProductId: string;
      if (facetValueLinks.length > 0) {
        if (variant.v1?.id || variant.v2?.id || variant.v3?.id) {
          // find the variantProductId with the most matches
          let minMatches = 1;
          if (variant.v1?.id && variant.v2?.id)
            minMatches = 2;
          if (variant.v1?.id && variant.v2?.id && variant.v3?.id)
            minMatches = 3;
          let maxMatchesIndex = null;

          // from facetValueLinks create new array with arrays sorted by variantProductId

          let facetValueLinksPerProductId = facetValueLinks.reduce((acc, curr) => {
            if (!acc[curr.variantProductId]) {
              acc[curr.variantProductId] = [];
            }
            acc[curr.variantProductId].push(curr);
            return acc;
          }, {});

          let arrayPerProductId: FacetValueLinks[] = Object.entries(facetValueLinksPerProductId).map((entry) => {
            return {
              variantProductId: entry[0] as string,
              facetValueLinks: entry[1] as unknown as ProductVariantToFacetValueLink[]
            };
          });

          for (let { variantProductId, facetValueLinks } of arrayPerProductId) {
            let maxMatches = 0;
            for (let i = 0; i < (facetValueLinks as []).length; i++) {
              let matches = 0;
              if (facetValueLinks[i].facetValueId === variant?.v1?.id) matches++;
              if (facetValueLinks[i].facetValueId === variant?.v2?.id) matches++;
              if (facetValueLinks[i].facetValueId === variant?.v3?.id) matches++;
              maxMatches = maxMatches + matches;
              if (maxMatches >= minMatches)
                maxMatchesIndex = i;
            }
          }

          if (maxMatchesIndex !== null) {
            variantProductId = facetValueLinks[maxMatchesIndex]?.variantProductId;
          }

        }
        if (variantProductId) {
          let product: Product = simpleDeepClone(await this.productService.getItemData(variantProductId));
          return product;
        }
        else return null;
      }
      else return null;
    }
    else return null;
  }

  async loadActiveFacetHolders() {

    let productVariants: Product[] = simpleDeepClone(await this.getAllProductVariantsOfProduct(this.baseProduct.id));

    let productVariantToFacetValues: ProductVariantToFacetValueLink[] = [];

    for (let link of productVariants) {

      let productVariantToFacetValueLink = (await lastValueFrom(this.productVariantToFacetValueService.getItems({
        searchFilter: {
          field: 'variantProductId',
          operator: Operator.equal,
          value: link.id
        }
      }).single$)).getProductVariantToFacetValueLinkList.items as ProductVariantToFacetValueLink[];

      productVariantToFacetValues = productVariantToFacetValues.concat(productVariantToFacetValueLink);

    };

    let facets: Facet[] = [];
    let facetValues: FacetValue[] = [];

    for (let variantToFacetValue of productVariantToFacetValues) {
      // Skip if facetValueId is null (handle bug gracefully)
      if (!variantToFacetValue.facetValueId) {
        console.warn('Found ProductVariantToFacetValueLink with null facetValueId:', variantToFacetValue);
        continue;
      }

      // check if facetValues doesnt already contain a facetValue with the id of the variantToFacetValue.facetValueId
      if (facetValues.find(fv => fv.id === variantToFacetValue.facetValueId)) continue;

      try {
        let facetValue = await this.facetValueService.getItemData(variantToFacetValue.facetValueId);
        if (facetValue && facetValue.facet) {
          facetValues.push(facetValue);
          facets.push(facetValue.facet);
        } else {
          console.warn('FacetValue or its facet is null for facetValueId:', variantToFacetValue.facetValueId);
        }
      } catch (error) {
        console.error('Failed to load facet value for ID:', variantToFacetValue.facetValueId, error);
        // Continue with other facet values instead of failing completely
      }
    }

    // filter duplicates from facets
    facets = facets.filter((facet, index, self) =>
      index === self.findIndex((t) => (
        t.id === facet.id
      ))
    );

    // filter duplicate facetValues
    facetValues = facetValues.filter((facetValue, index, self) =>
      index === self.findIndex((t) => (
        t.id === facetValue.id
      ))
    );

    // map facet to all facetValues that have the same facet 
    let facetHolders: FacetHolder[] = facets.map(facet => {
      let selectedValues = facetValues.filter(fv => fv.facet && fv.facet.id === facet.id);
      return { facet, selectedValues };
    });

    if (facetHolders.length > 0) {
      this.selectedFacets = facetHolders;
      this.cdRef.detectChanges();
    }
  }

  qualifyForSaveAll() {
    return this.allVariants().some(variant => variant?.existingProductVariant?.id && variant?.variant?.v1?.id);
  }

  qualifyForConnectionAndDelete() {
    return this.allVariants().some(variant => (variant?.existingProductVariant?.id === variant?.originalProductVariant?.id) && variant?.existingProductVariant?.id);
  }

  /**
   * Generate preview identifier for a variant based on delimiters
   */
  private generateVariantPreview(variant: FacetVariantWithProduct, baseIdentifier: string, delimiters: Record<number, string>): string {
    if (!variant.variant?.v1?.identifier) {
      return baseIdentifier;
    }

    let parts = [baseIdentifier];

    // Add each variant level with its delimiter
    if (variant.variant.v1?.identifier) {
      const delimiter = delimiters[0] || '';
      parts.push(delimiter + variant.variant.v1.identifier);
    }

    if (variant.variant.v2?.identifier) {
      const delimiter = delimiters[1] || '';
      parts.push(delimiter + variant.variant.v2.identifier);
    }

    if (variant.variant.v3?.identifier) {
      const delimiter = delimiters[2] || '';
      parts.push(delimiter + variant.variant.v3.identifier);
    }

    return parts.join('');
  }

  /**
   * Update delimiter for a specific facet position
   */
  onDelimiterChange(facetIndex: number, delimiter: string) {
    const currentDelimiters = this.facetDelimiters();
    this.facetDelimiters.set({
      ...currentDelimiters,
      [facetIndex]: delimiter
    });
  }

  /**
   * Get preview for a specific variant
   */
  getVariantPreview(variant: FacetVariantWithProduct) {
    const preview = this.variantPreviews().find(vp => vp.variant === variant);
    return preview || { variant, preview: '', result: null };
  }

  /**
   * Check if generate products button should be enabled
   */
  canGenerateProducts(): boolean {
    return this.generateProductsEnabled() && this.allVariants().some(variant =>
      variant.variant?.v1?.id && !variant.existingProductVariant?.id
    );
  }

  /**
   * Generate products for all variants without existing products (placeholder for backend logic)
   */
  async generateProductsForVariants() {
    if (!this.canGenerateProducts()) {
      return;
    }

    this.isGenerating.set(true);
    const currentResults = { ...this.generationResults() };

    try {
      // Get variants that need generation
      const variantsToGenerate = this.allVariants().filter(variant =>
        variant.variant?.v1?.id && !variant.existingProductVariant?.id
      );

      if (variantsToGenerate.length === 0) {
        this.baseServices.notificationService.warning(this.baseServices.i18nService.translate(_("product-variant.no-variants-found-for-generation")));
        return;
      }

      // Set all variants to generating state
      for (const variant of variantsToGenerate) {
        const variantKey = this.getVariantKey(variant);
        currentResults[variantKey] = { status: 'generating' };
      }
      this.generationResults.set({ ...currentResults });

      // Generate products sequentially to avoid overwhelming the backend
      for (const variant of variantsToGenerate) {
        const variantKey = this.getVariantKey(variant);

        try {
          await this.generateSingleVariantProduct(variant);

          currentResults[variantKey] = {
            status: 'success',
            message: 'Product created successfully',
            productId: variant.existingProductVariant?.id
          };

        } catch (error: any) {
          console.error('Failed to generate product for variant:', error);
          currentResults[variantKey] = {
            status: 'error',
            message: error?.message || 'Failed to create product'
          };
        }

        // Update results after each generation
        this.generationResults.set({ ...currentResults });
      }

      // Show summary notification
      const successCount = Object.values(currentResults).filter(r => r.status === 'success').length;
      const errorCount = Object.values(currentResults).filter(r => r.status === 'error').length;

      if (successCount > 0 && errorCount === 0) {
        this.baseServices.notificationService.success(this.baseServices.i18nService.translate(_("product-variant.generation-success"), { successCount }));
      } else if (successCount > 0 && errorCount > 0) {
        this.baseServices.notificationService.warning(this.baseServices.i18nService.translate(_("product-variant.generation-warning"), { successCount, errorCount }));
      } else {
        this.baseServices.notificationService.error(this.baseServices.i18nService.translate(_("product-variant.generation-error"), { errorCount }));
      }

    } catch (error: any) {
      console.error('Failed to generate products:', error);
      this.baseServices.notificationService.error(this.baseServices.i18nService.translate(_("product-variant.generation-failed"), { errorMessage: error?.message || 'Unknown error' }));
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Generate a single product for a variant
   */
  private async generateSingleVariantProduct(variant: FacetVariantWithProduct): Promise<void> {
    if (!this.baseProduct?.id) {
      throw new Error('No base product available');
    }

    // Generate the product identifier
    const delimiters = this.facetDelimiters();
    const baseIdentifier = this.baseProduct.identifier || 'P01';
    const generatedIdentifier = this.generateVariantPreview(variant, baseIdentifier, delimiters);

    // Create the product input
    const productInput: ProductInput = {
      identifier: generatedIdentifier,
      // description: `Generated variant product for ${this.getVariantDisplayName(variant)}`,
      variantParentId: this.baseProduct.id,
      productType: this.generateProductType(),
      // Copy other relevant properties from base product
      // primaryProductGroupId: this.baseProduct.primaryProductGroupId,
      unitId: this.baseProduct.unitId,
      // Add other properties as needed
    };

    // Save the product
    const createdProduct = await this.productService.saveItem(productInput);

    // Update the variant with the new product
    variant.existingProductVariant = simpleDeepClone(createdProduct);
    variant.originalProductVariant = simpleDeepClone(createdProduct);

    // Create facet value links
    const facetValuesToSave: ProductVariantToFacetValueLink[] = [];

    if (variant.variant?.v1?.id) {
      facetValuesToSave.push({
        variantProductId: createdProduct.id,
        facetValueId: variant.variant.v1.id
      });
    }
    if (variant.variant?.v2?.id) {
      facetValuesToSave.push({
        variantProductId: createdProduct.id,
        facetValueId: variant.variant.v2.id
      });
    }
    if (variant.variant?.v3?.id) {
      facetValuesToSave.push({
        variantProductId: createdProduct.id,
        facetValueId: variant.variant.v3.id
      });
    }

    if (facetValuesToSave.length > 0) {
      await this.productVariantToFacetValueService.saveItems(facetValuesToSave);
    }
  }

  /**
   * Clear generation results
   */
  clearGenerationResults(): void {
    this.generationResults.set({});
  }

  /**
   * Check if there are any generation results to display
   */
  hasGenerationResults(): boolean {
    return Object.keys(this.generationResults()).length > 0;
  }

  private getVariantKey(variant: FacetVariantWithProduct): string {
    return JSON.stringify(variant.variant);
  }

}
